'use strict';
import StringHelper from '../../global/StringHelper';
import sxQuery from '../../sxQuery/sxQuery';
import SxQueryUtils from '../../sxQuery/SxQueryUtils';
import DatePrecision from '../enums/DatePrecision';
import FilterPosition from '../enums/FilterPosition';
import UiColors from '../styles/UiColors';
import Helper from '../utils/Helper';
import CategoryTree from './CategoryTree';
import FilterCheckbox from './FilterCheckbox';
import Icons from './Icons';
import RangeSlider from './RangeSlider';
const ACTIVE_TOP_BUTTON_CLASSNAME = 'ss360-filter__button--active';
const createFilterId = (key, position) => `ss360-filter-${position}-${StringHelper.getSafeKey(key)}`;
var FilterType;
(function (FilterType) {
    FilterType["Multiselect"] = "multiselect";
    FilterType["Range"] = "range";
    FilterType["CategoryTree"] = "category-tree";
    FilterType["Color"] = "color";
    FilterType["Boolean"] = "boolean";
})(FilterType || (FilterType = {}));
const disableKeyboardActions = (wrapper) => {
    wrapper.find('button:not(.ss360-filter__button), input').attr('tabindex', '-1');
    wrapper.find("div[tabindex='0']").attr('tabindex', '-1');
};
const enableKeyboardActions = (wrapper) => {
    wrapper.find('button:not(.ss360-filter__button), input').attr('tabindex', null);
    wrapper.find("div[tabindex='-1']").attr('tabindex', '0');
};
const updateClearButton = (wrapper, isSelected, clearButton) => {
    if (clearButton === undefined) {
        clearButton = wrapper.find('.ss360-filter__clear-group');
    }
    if (isSelected) {
        clearButton.show();
        wrapper.addClass('ss360-filter__group--selected');
    }
    else {
        wrapper.removeClass('ss360-filter__group--selected');
        clearButton.hide();
    }
};
let currentId = 0;
class FilterGroupBuilder {
    constructor(context, onExpand, onChange, persistentFilterOptions, filterViewGroupCache, inlineValues, position, commitMethod, discardMethod) {
        this.context = context;
        this.pluginConfiguration = context.pluginConfiguration;
        this.onExpand = onExpand;
        this.onChange = onChange;
        this.persistentFilterOptions = persistentFilterOptions;
        this.filterViewGroupCache = filterViewGroupCache;
        this.inlineValues = inlineValues;
        this.position = position;
        this.commitMethod = commitMethod;
        this.discardMethod = discardMethod;
    }
    updateValueLabelAndInlineClear(wrapper, type, activeOptions, displayValue, displayUnit, value, isDate, datePrecision) {
        if ((value === undefined || value.length === 0) && wrapper !== undefined) {
            value = wrapper.find('.ss360-filter__button-value');
        }
        if (value === undefined || value.length === 0) {
            return;
        }
        let labelVal = this.pluginConfiguration.filters.allLabel;
        let hasOptions = false;
        if (activeOptions instanceof Array) {
            activeOptions = activeOptions.filter((opt) => opt !== undefined);
        }
        if ((type === FilterType.Multiselect || type === FilterType.Color) && activeOptions !== undefined) {
            if (typeof activeOptions === 'boolean') {
                if (displayValue !== undefined) {
                    labelVal = displayValue;
                    hasOptions = true;
                }
            }
            else if (activeOptions.length > 0) {
                labelVal = `${activeOptions[0]}${activeOptions.length > 1 ? ` +${activeOptions.length - 1}` : ''}`;
                hasOptions = true;
            }
        }
        else if (type === FilterType.CategoryTree && activeOptions !== undefined && activeOptions.length > 0) {
            const pathParts = activeOptions[0].split('/');
            labelVal = pathParts[pathParts.length - 1];
            hasOptions = true;
        }
        else if (type === FilterType.Range && activeOptions !== undefined && activeOptions.min !== undefined && activeOptions.max !== undefined) {
            if (isDate) {
                const factor = datePrecision === DatePrecision.MS ? 1 : 1000;
                const dateFormatLocale = this.context.pluginConfiguration.filters.dateFormatLocale;
                labelVal = `${new Date(activeOptions.min * factor).toLocaleDateString(dateFormatLocale)} - ${new Date(activeOptions.max * factor).toLocaleDateString(dateFormatLocale)}`;
            }
            else {
                const minLabel = Helper.addUnitToNumber(activeOptions.min, Helper.isUsdOrPound(displayUnit) ? displayUnit : undefined);
                const maxLabel = Helper.addUnitToNumber(activeOptions.max, displayUnit);
                labelVal = (minLabel === maxLabel ? `${minLabel}` : `${minLabel} - ${maxLabel}`).trim();
            }
            hasOptions = true;
        }
        if (hasOptions && wrapper !== undefined) {
            wrapper.find('.ss360-filter__button-clear').show();
        }
        else if (!hasOptions && wrapper !== undefined) {
            wrapper.find('.ss360-filter__button-clear').hide();
        }
        value.text(labelVal);
    }
    ;
    hideGroup(viewGroupKey, toggling = false, preventDiscard = false) {
        const cacheEntry = this.filterViewGroupCache.groupByKey[viewGroupKey];
        if (cacheEntry !== undefined || cacheEntry !== null) {
            const wrapper = cacheEntry.wrapper;
            const content = cacheEntry.content;
            const button = cacheEntry.button;
            const icon = cacheEntry.icon;
            SxQueryUtils.requestAnimationFrame(() => {
                wrapper.removeClass('ss360-filter__group--active');
                content.css('opacity', null);
                if (toggling) {
                    button.removeClass(ACTIVE_TOP_BUTTON_CLASSNAME);
                }
                else {
                    button.removeClass('ss360-filter__button--active');
                }
                content.removeClass('ss360-filter__content--active');
                icon.removeClass('ss360-filter__button-icon--active');
            });
        }
        if (this.filterViewGroupCache.activeGroups.indexOf(viewGroupKey) !== -1) {
            this.filterViewGroupCache.activeGroups.splice(this.filterViewGroupCache.activeGroups.indexOf(viewGroupKey), 1);
        }
        if (!preventDiscard && this.discardMethod !== undefined) {
            this.discardMethod();
        }
    }
    showGroup(viewGroupKey, hideOthers = false) {
        if (hideOthers) {
            this.filterViewGroupCache.activeGroups.forEach((groupKey) => {
                this.hideGroup(groupKey, true);
            });
        }
        const cacheEntry = this.filterViewGroupCache.groupByKey[viewGroupKey];
        SxQueryUtils.requestAnimationFrame(() => {
            cacheEntry.wrapper.addClass('ss360-filter__group--active');
            cacheEntry.icon.addClass('ss360-filter__button-icon--active');
            cacheEntry.button.addClass('ss360-filter__button--active');
            setTimeout(() => {
                cacheEntry.content.addClass('ss360-filter__content--active');
            }, 300);
            cacheEntry.content.css('opacity', '1');
        });
        if (this.filterViewGroupCache.activeGroups.indexOf(viewGroupKey) === -1) {
            this.filterViewGroupCache.activeGroups.push(viewGroupKey);
        }
    }
    reposition(cacheEntry, offsetLeft) {
        const contentLayer = cacheEntry.content;
        const layer = this.filterViewGroupCache.layer;
        SxQueryUtils.requestAnimationFrame(() => {
            if (offsetLeft === undefined) {
                // @ts-ignore
                const layerWidth = layer.width();
                const buttonLayerOffset = cacheEntry.button === undefined ? 0 : cacheEntry.button.offset().left - layer.offset().left;
                const buttonHeightOffset = cacheEntry.button === undefined ? 0 : cacheEntry.button.outerHeight();
                const MAX_CONTENT_WIDTH = 300;
                if (buttonLayerOffset + MAX_CONTENT_WIDTH < layerWidth) {
                    offsetLeft = 0;
                }
                else {
                    offsetLeft = layerWidth - buttonLayerOffset - MAX_CONTENT_WIDTH;
                }
                cacheEntry.offsetLeft = offsetLeft;
                contentLayer.css('left', offsetLeft);
                contentLayer.css('top', buttonHeightOffset);
            }
        });
    }
    toggleGroup(viewGroupKey, preventDiscard = false) {
        const cacheEntry = this.filterViewGroupCache.groupByKey[viewGroupKey];
        const wrapper = cacheEntry.wrapper;
        const wasActive = wrapper.hasClass('ss360-filter__group--active');
        const $bodyAndContent = sxQuery('body, .ss360-layer__content');
        const button = cacheEntry.button;
        const eventId = this.context.generateId('ss360ToggleGroup');
        if (wasActive) {
            if (button !== undefined) {
                button.removeClass(ACTIVE_TOP_BUTTON_CLASSNAME);
                button.attr('aria-expanded', 'false');
            }
            this.hideGroup(viewGroupKey, false, preventDiscard);
            this.persistentFilterOptions.ss360ActiveGroup = undefined;
            $bodyAndContent.off(`click.${eventId}, keydown.${eventId}`);
            return;
        }
        button.attr('aria-expanded', 'true');
        if (button !== undefined) {
            button.addClass(ACTIVE_TOP_BUTTON_CLASSNAME);
        }
        this.persistentFilterOptions.ss360ActiveGroup = viewGroupKey;
        $bodyAndContent.off(`click.${eventId}, keydown.${eventId}`);
        this.showGroup(viewGroupKey, true);
        let offsetLeft = cacheEntry.offsetLeft;
        if (offsetLeft === undefined) {
            const resizeEventName = `resize.${this.context.generateId('ss360RecalculateStyles')}`;
            sxQuery(window).off(resizeEventName).on(resizeEventName, () => {
                Object.keys(this.filterViewGroupCache.groupByKey).forEach((group) => {
                    const entry = this.filterViewGroupCache.groupByKey[group];
                    entry.offsetLeft = undefined;
                });
                sxQuery(window).off(resizeEventName);
            });
        }
        if (offsetLeft === undefined) {
            this.reposition(cacheEntry, offsetLeft);
        }
        wrapper.on('click', (e) => {
            e.stopPropagation();
        });
        $bodyAndContent.on(`click.${eventId}`, (e) => {
            if (sxQuery(e.target).is(wrapper) || sxQuery(e.target).parents('.ss360-filter__content').length > 0) {
                return; // self click
            }
            this.toggleGroup(viewGroupKey);
        });
        $bodyAndContent.on(`keydown.${eventId}`, (e) => {
            if (e.key === 'Escape' || e.code === 'Escape') {
                this.toggleGroup(viewGroupKey);
                e.stopPropagation();
                e.preventDefault();
            }
        });
        if (this.onExpand !== undefined && typeof this.onExpand === 'function') {
            this.onExpand();
        }
        wrapper.off('click.ss360PreventBubble');
        wrapper.on('click.ss360PreventBubble', (e) => {
            if (sxQuery(e.target).parents('.ss360-filter__content').length === 0) {
                e.preventDefault();
                e.stopPropagation();
            }
        });
        if (this.filterViewGroupCache.filters !== undefined) {
            this.filterViewGroupCache.filters.forEach((group) => {
                group.mount();
            });
        }
    }
    createOrGetWrapper(viewGroupKey, key, label, type, hasSelectedOptions, activeOptions, displayValue, displayUnit, isDate, datePrecision) {
        const position = this.position;
        const filterId = createFilterId(viewGroupKey, this.position);
        const shouldShow = position === 'left' && this.persistentFilterOptions.expandedFilters.indexOf(viewGroupKey) !== -1;
        const cacheEntry = this.filterViewGroupCache.groupByKey[viewGroupKey];
        const entryExists = cacheEntry !== undefined;
        let wrapper;
        if (entryExists) {
            wrapper = cacheEntry.wrapper;
            if (!shouldShow && wrapper.hasClass('ss360-filter__group--active')) {
                this.hideGroup(viewGroupKey, false, true);
            }
            else if (shouldShow && !wrapper.hasClass('ss360-filter__group--active')) {
                this.showGroup(viewGroupKey, false);
                wrapper.find('.ss360-filter__content').css('height', null);
            }
        }
        else {
            const plainWrapper = document.createElement('section');
            plainWrapper.setAttribute('id', filterId);
            plainWrapper.className = `ss360-n-section ss360-filter__group ss360-${type}-filter ss360-${type}-filter--${position} ${shouldShow ? 'ss360-filter__group--active' : ''}`;
            plainWrapper.setAttribute('aria-label', label);
            wrapper = sxQuery(plainWrapper);
        }
        let icon;
        if (entryExists) {
            icon = cacheEntry.icon;
        }
        else {
            const iconPlain = document.createElement('i');
            const dimension = this.position === FilterPosition.Top ? 16 : 20;
            const viewBox = this.position === FilterPosition.Top ? '6 0 16 20' : undefined;
            const iconSvg = Icons.getSvgIconNode(Icons.SIMPLE_ARROW, UiColors.GREY_TEXT, undefined, dimension, dimension, viewBox);
            iconPlain.className = `ss360-filter__button-icon ${shouldShow ? 'ss360-filter__button-icon--active' : ''}`;
            iconPlain.setAttribute('aria-hidden', 'true');
            iconPlain.appendChild(iconSvg);
            icon = sxQuery(iconPlain);
        }
        let button;
        if (entryExists) {
            button = cacheEntry.button;
        }
        else {
            const buttonPlain = document.createElement('button');
            buttonPlain.className = 'ss360-filter__button ss360-flex ss360-flex--align-center ss360-n-button';
            buttonPlain.setAttribute('type', 'button');
            buttonPlain.innerText = label;
            buttonPlain.setAttribute('id', `${filterId}-button`);
            buttonPlain.setAttribute('aria-controls', `${filterId}-content`);
            buttonPlain.setAttribute('aria-expanded', shouldShow ? 'true' : 'false');
            button = sxQuery(buttonPlain);
        }
        let value;
        if (this.inlineValues && entryExists) {
            value = cacheEntry.value;
            this.updateValueLabelAndInlineClear(undefined, type, activeOptions, displayValue, displayUnit, value, isDate, datePrecision);
        }
        else if (this.inlineValues) {
            const valuePlain = document.createElement('span');
            valuePlain.className = 'ss360-filter__button-value';
            value = sxQuery(valuePlain);
            this.updateValueLabelAndInlineClear(undefined, type, activeOptions, displayValue, displayUnit, value, isDate, datePrecision);
        }
        let inlineClear;
        if (this.inlineValues && entryExists) {
            inlineClear = cacheEntry.inlineClear;
            if (!hasSelectedOptions) {
                inlineClear.hide();
            }
            else {
                inlineClear.show();
            }
        }
        else if (this.inlineValues) {
            const inlineClearPlain = document.createElement('span');
            inlineClearPlain.className = 'ss360-filter__button-clear';
            inlineClearPlain.setAttribute('role', 'button');
            inlineClearPlain.setAttribute('aria-label', this.pluginConfiguration.filters.clearGroupAriaLabel.replace('#FILTER_NAME#', label));
            inlineClearPlain.setAttribute('tabindex', '0');
            const i = document.createElement('i');
            i.className = 'ss360-filter__button-clear-icon';
            i.setAttribute('aria-hidden', 'true');
            const iconSvg = Icons.getSvgIconNode(Icons.CROSS, UiColors.GREY_TEXT, undefined, 16, 16);
            i.appendChild(iconSvg);
            inlineClearPlain.appendChild(i);
            inlineClear = sxQuery(inlineClearPlain);
            const clear = (e) => {
                e.stopPropagation();
                e.preventDefault();
                this.onChange(key, undefined, label, true);
                inlineClear.hide();
            };
            inlineClear.on('keydown', (e) => {
                if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'Space') {
                    clear(e);
                }
            });
            inlineClear.on('click', (e) => {
                clear(e);
            });
            if (!hasSelectedOptions) {
                inlineClear.hide();
            }
        }
        if (!entryExists && position === 'top') {
            if (shouldShow) {
                button.addClass(ACTIVE_TOP_BUTTON_CLASSNAME);
            }
        }
        let content;
        if (entryExists) {
            content = cacheEntry.content;
        }
        else {
            const contentPlain = document.createElement('div');
            contentPlain.className = `ss360-filter__content${shouldShow ? ' ss360-filter__content--active' : ''}`;
            contentPlain.setAttribute('id', `${filterId}-content`);
            contentPlain.setAttribute('aria-labelledby', `${filterId}-button`);
            content = sxQuery(contentPlain);
        }
        let skipButton;
        if (entryExists) {
            skipButton = cacheEntry.skipButton;
        }
        else {
            const skipButtonPlain = document.createElement('button');
            skipButtonPlain.className = 'ss360-skip-link ss360-skip-link--group ss360-n-button';
            skipButtonPlain.setAttribute('type', 'button');
            const { skipFilterGroup, skipFilterGroupAriaLabel } = this.pluginConfiguration.filters;
            skipButtonPlain.innerText = skipFilterGroup;
            skipButtonPlain.setAttribute('aria-label', skipFilterGroupAriaLabel.replace('#FILTER_NAME#', label));
            skipButton = sxQuery(skipButtonPlain);
        }
        let clearButton;
        if (entryExists) {
            clearButton = cacheEntry.clearButton;
        }
        else {
            const clearButtonPlain = document.createElement('button');
            clearButtonPlain.className = 'ss360-filter__clear-group ss360-n-button';
            clearButtonPlain.setAttribute('type', 'button');
            clearButtonPlain.setAttribute('aria-label', this.pluginConfiguration.filters.clearGroupAriaLabel.replace('#FILTER_NAME#', label));
            const i = document.createElement('i');
            i.innerHTML = '&times;';
            clearButtonPlain.appendChild(i);
            clearButtonPlain.innerHTML += ` ${this.pluginConfiguration.filters.clearGroupLabel}`;
            clearButton = sxQuery(clearButtonPlain);
        }
        updateClearButton(wrapper, hasSelectedOptions, clearButton);
        if (entryExists) {
            clearButton.off('click.ss360ClearFilter');
        }
        clearButton.on('click.ss360ClearFilter', () => {
            this.onChange(key, undefined, label, true);
            clearButton.fadeOut();
            const $search = wrapper.find('.ss360-multiselect__search');
            $search.val('');
            $search._trigger('input');
        });
        if (entryExists) {
            content.empty();
        }
        else {
            if (value) {
                button.append(value);
            }
            button.append(icon);
            if (inlineClear) {
                button.append(inlineClear);
            }
            wrapper.append(button);
            wrapper.append(content);
            skipButton.on('click', (e) => {
                const groupParent = e.target.parentNode.parentNode;
                if (groupParent.nextSibling === null) {
                    this.context.layer.get().focusFirstResult();
                }
                else {
                    sxQuery(groupParent.nextSibling).find('.ss360-filter__button').focus();
                }
            });
        }
        content.prepend(clearButton); // should be shown just after the skip button (tab order)
        content.prepend(skipButton);
        this.filterViewGroupCache.groupByKey[viewGroupKey] = {
            wrapper,
            content,
            button,
            icon,
            skipButton,
            clearButton,
            value,
            inlineClear
        };
        if (position === 'top' && !entryExists) {
            button.on('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleGroup(viewGroupKey);
            });
        }
        else if (position === 'left') {
            const activeGroupIdx = this.filterViewGroupCache.activeGroups.indexOf(viewGroupKey);
            if (!shouldShow) {
                disableKeyboardActions(wrapper);
                content.css('height', 0);
                content.data('heightcache', 'auto');
                content.attr('aria-hidden', 'true');
                content.attr('inert', 'inert');
                button.attr('aria-expanded', 'false');
                if (activeGroupIdx !== -1) {
                    this.filterViewGroupCache.activeGroups.splice(activeGroupIdx, 1);
                }
            }
            else {
                content.attr('aria-hidden', 'false');
                content.attr('inert', null);
                button.attr('aria-expanded', 'true');
                if (activeGroupIdx === -1) {
                    this.filterViewGroupCache.activeGroups.push(viewGroupKey);
                }
            }
            if (!entryExists) {
                button.on('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (this.filterViewGroupCache.activeGroups.indexOf(viewGroupKey) !== -1) { // hide
                        this.fullyHide(viewGroupKey, wrapper, content);
                    }
                    else { // show
                        this.fullyShow(viewGroupKey, wrapper, content);
                    }
                });
            }
        }
        return wrapper;
    }
    fullyHide(viewGroupKey, wrapper, content) {
        this.hideGroup(viewGroupKey);
        const outerHeight = content.outerHeight();
        if (content.data('heightcache') === undefined) { // don't cache if the animation wasn't completed
            content.data('heightcache', outerHeight);
        }
        content.css('height', outerHeight);
        if (this.position === FilterPosition.Left) {
            content.attr('aria-hidden', 'true');
            content.attr('inert', 'inert');
        }
        wrapper.find('.ss360-filter__button').attr('aria-expanded', 'false');
        SxQueryUtils.requestAnimationFrame(() => {
            content.css('height', 0);
        });
        const idx = this.persistentFilterOptions.expandedFilters.indexOf(viewGroupKey);
        if (idx !== -1) {
            this.persistentFilterOptions.expandedFilters.splice(idx, 1);
        }
        disableKeyboardActions(wrapper);
    }
    fullyShow(viewGroupKey, wrapper, content) {
        this.showGroup(viewGroupKey, false);
        content.attr('aria-hidden', 'false');
        content.attr('inert', null);
        wrapper.find('.ss360-filter__button').attr('aria-expanded', 'true');
        content.css('height', content.data('heightcache'));
        setTimeout(() => {
            content.css('height', 'auto');
            content.data('heightcache', null);
        }, 300);
        if (this.persistentFilterOptions.expandedFilters.indexOf(viewGroupKey) === -1) {
            this.persistentFilterOptions.expandedFilters.push(viewGroupKey);
        }
        enableKeyboardActions(wrapper);
    }
    generateId() {
        currentId++;
        return currentId;
    }
    multipleChoice(viewGroupKey, key, label, options, activeOptions, counts, isColorFilter, filterModificationMap = {}, isBooleanFilter = false, disableSearch = false) {
        activeOptions = isBooleanFilter ? activeOptions : activeOptions || [];
        let displayValue;
        const setDisplayValue = () => {
            if (isBooleanFilter && activeOptions !== undefined) {
                // @ts-ignore
                displayValue = options.filter((option) => option.value === activeOptions)[0].name;
            }
        };
        setDisplayValue();
        const wrapper = this.createOrGetWrapper(viewGroupKey, key, label, FilterType.Multiselect, isBooleanFilter ? activeOptions !== undefined : activeOptions.length > 0, activeOptions, displayValue);
        const inputWrapper = sxQuery('<div role="group" class="ss360-multiselect"></div>');
        const filterConfig = this.pluginConfiguration.filters;
        const filterPosition = this.position;
        const checkboxWrapper = sxQuery(`<div class="ss360-multiselect__wrap ss360-flex ${filterPosition === FilterPosition.Left
            ? 'ss360-flex--nowrap ss360-flex--column' : 'ss360-flex--wrap'}"></div>`);
        let searchDisplayed = false;
        let emptyStateElement;
        let selectedValues = activeOptions;
        const updateSelectedValues = () => {
            selectedValues = activeOptions;
            if (this.pluginConfiguration.results.nameParsing && !isBooleanFilter && selectedValues.length > 0) { // get the original case for displaying and updating
                const caseMap = options.reduce((acc, option) => {
                    acc[option.name.toLowerCase()] = option.name;
                    return acc;
                }, {});
                selectedValues = selectedValues.map((val) => (caseMap[val] === undefined ? val : caseMap[val]));
            }
        };
        const updateDisplayValue = (displayValueMap = {}) => {
            if (this.inlineValues) {
                setDisplayValue();
                let passedActiveOptions = activeOptions;
                if (displayValueMap !== undefined && !isBooleanFilter) {
                    passedActiveOptions = activeOptions.map((val) => displayValueMap[val] || val);
                }
                this.updateValueLabelAndInlineClear(wrapper, FilterType.Multiselect, passedActiveOptions, displayValue);
            }
        };
        updateSelectedValues();
        const checkboxes = {};
        const shouldOfferSearch = options.length > filterConfig.multiSelectSearchThreshold;
        const optionsToShow = filterConfig.multiSelectShowMoreThreshold;
        const getIsExpanded = () => options.length <= optionsToShow || this.filterViewGroupCache.expandedGroups[key] === true;
        let isExpanded = getIsExpanded();
        let showMoreButton;
        let availableOptions = options.length;
        const getOptionKey = (option) => {
            const val = option.value !== undefined ? option.value : option.key;
            if (val !== undefined && val !== null) {
                return `${val}`;
            }
            return val;
        };
        const runQueryFilter = (filterQuery) => {
            if (this.context.pluginConfiguration.callbacks.filterSearch) {
                try {
                    filterQuery = this.context.pluginConfiguration.callbacks.filterSearch(key, filterQuery) || filterQuery;
                }
                catch (e) {
                    // ccl
                }
            }
            let matchingOptionCount = 0;
            options.forEach((option) => {
                const checkbox = checkboxes[getOptionKey(option)];
                if (checkbox === undefined) {
                    return;
                }
                const matches = filterQuery.length === 0 || option.name.toLowerCase().indexOf(filterQuery) !== -1; // indexOf seems to perform better than regexp in this case
                if (matches) {
                    matchingOptionCount++;
                    if (isExpanded || matchingOptionCount <= optionsToShow) {
                        checkbox.show();
                    }
                    else {
                        checkbox.hide();
                    }
                }
                else {
                    checkbox.hide();
                }
            });
            availableOptions = matchingOptionCount;
            updateShowMoreButton();
            if (matchingOptionCount === 0 && emptyStateElement !== undefined) {
                emptyStateElement.show();
            }
            else if (emptyStateElement !== undefined) {
                emptyStateElement.hide();
            }
        };
        const updateShowMoreButton = () => {
            if (showMoreButton === undefined)
                return;
            if (availableOptions <= optionsToShow) {
                showMoreButton.hide();
                return;
            }
            showMoreButton.show();
            const diffString = (availableOptions - optionsToShow).toString();
            const text = !isExpanded ? filterConfig.multiSelectShowMoreLabel : filterConfig.multiSelectShowLessLabel;
            const ariaLabel = !isExpanded ? filterConfig.multiSelectShowMoreAriaLabel : filterConfig.multiSelectShowLessAriaLabel;
            showMoreButton.attr('aria-label', ariaLabel.replace(/#FILTER_NAME#/g, label).replace(/#COUNT#/g, diffString));
            showMoreButton.text(text.replace(/#COUNT#/g, diffString));
        };
        const removeSearch = () => {
            inputWrapper.find('.ss360-multiselect__search, .ss360-multiselect__empty-state').remove();
            searchDisplayed = false;
        };
        const renderSearch = () => {
            if (searchDisplayed) {
                return;
            }
            const filterInput = sxQuery(`<input type="search" class="ss360-multiselect__search">`);
            const placeholder = filterConfig.multiSelectSearchLabel.replace(/#FILTER_NAME#/g, label);
            filterInput.attr('aria-label', placeholder);
            filterInput.attr('placeholder', placeholder);
            if (this.filterViewGroupCache.searchQueries[key] !== undefined) {
                filterInput.val(this.filterViewGroupCache.searchQueries[key]);
            }
            inputWrapper.prepend(filterInput);
            emptyStateElement = sxQuery(`<span style="display:none;" class="ss360-multiselect__empty-state">${filterConfig.multiSelectEmptyState}</span>`);
            inputWrapper.append(emptyStateElement);
            filterInput.on('input', (e) => {
                // @ts-ignore
                const filterQuery = sxQuery(e.target).val().trim().toLowerCase();
                this.filterViewGroupCache.searchQueries[key] = filterQuery;
                runQueryFilter(filterQuery);
            });
            searchDisplayed = true;
        };
        if (shouldOfferSearch && !disableSearch) {
            renderSearch();
        }
        const onCheckboxChange = (value, checked) => {
            // @ts-ignore
            let idx = isBooleanFilter ? -1 : selectedValues.indexOf(value);
            if (!isBooleanFilter && idx === -1 && !checked) {
                Object.keys(filterModificationMap).forEach((displayVal) => {
                    const realVal = filterModificationMap[displayVal];
                    if (realVal === value) {
                        // @ts-ignore
                        idx = selectedValues.indexOf(displayVal);
                    }
                });
            }
            if (checked && idx === -1 && !isBooleanFilter) {
                // @ts-ignore
                selectedValues.push(value);
            }
            else if (!checked && idx !== -1 && !isBooleanFilter) {
                // @ts-ignore
                selectedValues.splice(idx, 1);
            }
            if (!isBooleanFilter) {
                selectedValues = selectedValues.map((val) => filterModificationMap[val] || val);
            }
            const displayValueMap = {};
            if (!isBooleanFilter && this.inlineValues) {
                options.forEach((o) => {
                    if (o.value && o.name && o.value !== o.name) {
                        displayValueMap[o.value] = o.name;
                    }
                });
            }
            if (this.onChange !== undefined && typeof this.onChange === 'function') {
                let filterValue = selectedValues;
                if (isBooleanFilter) {
                    // @ts-ignore
                    filterValue = checked ? value === 'true' || value === true : undefined;
                }
                this.onChange((key || label), filterValue, label);
            }
            if (this.commitMethod === undefined) {
                updateDisplayValue(displayValueMap);
            }
        };
        let hasColor = false;
        let hasIcon = false;
        const { showCounts } = filterConfig;
        const safeKey = StringHelper.getSafeKey(key);
        const createCheckbox = (option, idx) => {
            const id = `ss360-checkbox-${safeKey}-${this.generateId()}`;
            let c = undefined;
            if (showCounts && option.count !== undefined) {
                c = option.count;
            }
            else if (showCounts) {
                c = counts[option.key];
            }
            let count = showCounts && c !== undefined ? c : option.count;
            if (!showCounts) {
                count = undefined;
            }
            // @ts-ignore
            const isChecked = isBooleanFilter ? option.value === selectedValues : selectedValues.indexOf(option.key) !== -1;
            const optionKey = getOptionKey(option);
            const checkbox = new FilterCheckbox(optionKey, option.name, count, isChecked, id, isColorFilter && option.color !== undefined ? option.color : undefined, option.image, isExpanded || idx < optionsToShow, onCheckboxChange, filterConfig.countAriaLabel);
            if (isColorFilter && option.color !== undefined) {
                hasColor = true;
            }
            if (option.image) {
                hasIcon = true;
            }
            checkboxWrapper.append(checkbox.getDomNode());
            checkboxes[optionKey] = checkbox;
            return checkbox;
        };
        options.forEach((option, idx) => {
            createCheckbox(option, idx);
        });
        if (shouldOfferSearch && this.filterViewGroupCache.searchQueries[key] !== undefined) {
            runQueryFilter(this.filterViewGroupCache.searchQueries[key]);
        }
        inputWrapper.append(checkboxWrapper);
        const renderShowMoreButton = () => {
            if (inputWrapper.find('.ss360-multiselect__show-more').length === 0) {
                showMoreButton = sxQuery('<button type="button" class="ss360-n-button ss360-multiselect__show-more"></button>');
                showMoreButton.on('click', () => {
                    isExpanded = !isExpanded;
                    this.filterViewGroupCache.expandedGroups[key] = isExpanded;
                    const searchQuery = this.filterViewGroupCache.searchQueries[key] !== undefined ? this.filterViewGroupCache.searchQueries[key] : '';
                    runQueryFilter(searchQuery);
                });
                inputWrapper.append(showMoreButton);
            }
            updateShowMoreButton();
        };
        if (options.length > optionsToShow) {
            renderShowMoreButton();
        }
        if (hasColor) {
            inputWrapper.addClass('ss360-multiselect--color');
        }
        if (hasIcon) {
            inputWrapper.addClass('ss360-multiselect--icon');
        }
        if (hasColor || hasIcon) {
            inputWrapper.addClass('ss360-multiselect--has-color-or-icon');
        }
        const content = this.filterViewGroupCache.groupByKey[viewGroupKey].content;
        content.append(inputWrapper);
        this.appendApplyButton(viewGroupKey, content);
        const self = this;
        return {
            mount() { },
            addClass(className) {
                wrapper.addClass(className);
            },
            getWrapper() {
                return wrapper;
            },
            show: () => {
                this.fullyShow(viewGroupKey, wrapper, wrapper.find('.ss360-filter__content'));
            },
            hide: () => {
                this.fullyHide(viewGroupKey, wrapper, wrapper.find('.ss360-filter__content'));
            },
            update: (filterGroup, active, modificationMap) => {
                if (!isBooleanFilter) {
                    active = active || [];
                    updateClearButton(wrapper, active.length > 0);
                }
                else {
                    active = active && active !== undefined;
                    updateClearButton(wrapper, active);
                }
                options = (filterGroup.values || []);
                availableOptions = options.length;
                activeOptions = active;
                counts = filterGroup.counts || counts;
                filterModificationMap = modificationMap || filterModificationMap;
                updateDisplayValue();
                updateSelectedValues();
                if (options.length > filterConfig.multiSelectSearchThreshold && !disableSearch) {
                    renderSearch();
                }
                else {
                    removeSearch();
                    this.filterViewGroupCache.searchQueries[key] = '';
                }
                const flatOptions = options.map((option) => getOptionKey(option));
                const optionNameMap = {};
                const localCountMap = {};
                options.forEach((option) => {
                    const key = getOptionKey(option);
                    optionNameMap[key] = option.name;
                    if (showCounts && option.count !== undefined) {
                        localCountMap[key] = option.count;
                    }
                });
                let isVisible = false;
                Object.keys(checkboxes).forEach((key) => {
                    const checkbox = checkboxes[key];
                    if (checkbox === undefined) {
                        return;
                    }
                    if (flatOptions.indexOf(key) === -1) {
                        checkbox.remove();
                        checkboxes[key] = undefined;
                    }
                    else {
                        isVisible = true;
                        const valueKey = optionNameMap[key];
                        checkbox.updateActive(isBooleanFilter ? active : activeOptions.indexOf(valueKey) !== -1);
                        checkbox.updateCount(showCounts ? counts[key] || localCountMap[key] : undefined);
                    }
                });
                flatOptions.forEach((key, idx) => {
                    if (checkboxes[key] !== undefined) {
                        return;
                    }
                    createCheckbox(options.filter((option) => getOptionKey(option) === key)[0], idx);
                });
                flatOptions.forEach((key, idx) => {
                    const checkbox = checkboxes[key];
                    if (checkbox === undefined) {
                        return;
                    }
                    checkbox.ensureLastPosition(checkboxWrapper);
                });
                if (options.length > optionsToShow) {
                    renderShowMoreButton();
                }
                if (isBooleanFilter) {
                    Object.values(checkboxes).forEach((checkbox) => {
                        if (checkbox !== undefined) {
                            // @ts-ignore
                            checkbox.updateActive(active === checkbox.key);
                        }
                    });
                }
                isExpanded = getIsExpanded();
                runQueryFilter(this.filterViewGroupCache.searchQueries[key] || '');
                if (this.position === FilterPosition.Top) {
                    this.reposition(this.filterViewGroupCache.groupByKey[viewGroupKey]);
                }
                return isVisible || isBooleanFilter;
            },
            updateHandlers(onChange, onExpand, commitMethod, discardMethod) {
                self.onChange = onChange;
                self.onExpand = onExpand;
                self.commitMethod = commitMethod;
                self.discardMethod = discardMethod;
            }
        };
    }
    range(viewGroupKey, key, label, min, max, step, unit, activeValues, singleFilterSetting, counts, isDate, datePrecision, fractionalDigits, maxResults) {
        const wrapper = this.createOrGetWrapper(viewGroupKey, key, label, FilterType.Range, (activeValues || {}).min !== undefined, activeValues, undefined, unit, isDate, datePrecision);
        const sliderWrapper = sxQuery('<div class="ss360-slider"></div>');
        let drawHistogram;
        if (singleFilterSetting.drawHistogram !== undefined) {
            drawHistogram = singleFilterSetting.drawHistogram;
        }
        else if (isDate) {
            drawHistogram = false;
        }
        let minCache = activeValues !== undefined ? activeValues.min : min;
        let maxCache = activeValues !== undefined ? activeValues.max : max;
        this.filterViewGroupCache.groupByKey[viewGroupKey].content.append(sliderWrapper);
        const decimalPlaces = singleFilterSetting.decimalPlaces !== undefined ? singleFilterSetting.decimalPlaces : fractionalDigits;
        const slider = new RangeSlider(sliderWrapper, {
            min,
            max,
            step,
            unit,
            drawHistogram,
            isDate,
            showSlider: singleFilterSetting.showSlider !== undefined ? singleFilterSetting.showSlider : true,
            prefix: singleFilterSetting.prefix,
            postfix: singleFilterSetting.postfix,
            decimalPlaces,
            datePrecision,
            filterName: label
        }, counts, (newMin, newMax, isButton = false) => {
            if (minCache !== newMin || maxCache !== newMax) {
                minCache = newMin;
                maxCache = newMax;
                if (this.onChange !== undefined && typeof this.onChange === 'function') {
                    const obj = min === newMin && max === newMax ? undefined : {
                        min: newMin,
                        max: newMax
                    }; // empty object to notify that we can ignore this filter
                    this.onChange((key || label), obj, label);
                }
            }
            if (isButton && this.commitMethod) {
                this.commitMethod();
            }
        }, this.context, wrapper, maxResults);
        if (activeValues !== undefined) {
            slider.setCurrentMin(Math.max(activeValues.min, min));
            slider.setCurrentMax(Math.min(activeValues.max, max));
            sliderWrapper.addClass('ss360-slider--selected');
        }
        const self = this;
        return {
            mount() { slider.mount(); },
            addClass(className) { wrapper.addClass(className); },
            getWrapper() { return wrapper; },
            show: () => {
                this.fullyShow(viewGroupKey, wrapper, wrapper.find('.ss360-filter__content'));
            },
            hide: () => {
                this.fullyHide(viewGroupKey, wrapper, wrapper.find('.ss360-filter__content'));
            },
            update: (filterGroup, active) => {
                if (filterGroup.min === filterGroup.max && (active === undefined || (active.min === undefined && active.max === undefined))) {
                    return false;
                }
                this.updateValueLabelAndInlineClear(wrapper, FilterType.Range, active, undefined, unit, undefined, isDate, datePrecision);
                updateClearButton(wrapper, active !== undefined && active.min !== undefined);
                slider.updateMinMax(filterGroup.min, filterGroup.max, active ? active.min : undefined, active ? active.max : undefined, filterGroup.counts);
                if (this.position === FilterPosition.Top) {
                    this.reposition(this.filterViewGroupCache.groupByKey[viewGroupKey]);
                }
                return true;
            },
            updateHandlers(onChange, onExpand, commitMethod, discardMethod) {
                self.onChange = onChange;
                self.onExpand = onExpand;
                self.commitMethod = commitMethod;
                self.discardMethod = discardMethod;
            }
        };
    }
    categoryTree(viewGroupKey, key, label, values, activeValue) {
        const wrapper = this.createOrGetWrapper(viewGroupKey, key, label, FilterType.CategoryTree, (activeValue || []).length > 0, activeValue);
        const treeWrapper = sxQuery('<div class="ss360-category-tree-wrapper"></div>');
        const content = this.filterViewGroupCache.groupByKey[viewGroupKey].content;
        content.append(treeWrapper);
        this.appendApplyButton(viewGroupKey, content);
        const categoryTree = new CategoryTree(treeWrapper, values, activeValue, this.pluginConfiguration, (val) => {
            if (this.onChange !== undefined && typeof this.onChange === 'function') {
                const obj = val === undefined ? undefined : [val.toString()];
                this.onChange((key || label), obj, label);
            }
        });
        const self = this;
        return {
            mount() { },
            addClass(className) { wrapper.addClass(className); },
            getWrapper() { return wrapper; },
            show: () => {
                this.fullyShow(viewGroupKey, wrapper, wrapper.find('.ss360-filter__content'));
            },
            hide: () => {
                this.fullyHide(viewGroupKey, wrapper, wrapper.find('.ss360-filter__content'));
            },
            update: (_filterGroup, active, values) => {
                updateClearButton(wrapper, (active || []).length > 0);
                categoryTree.update(values, active);
                this.updateValueLabelAndInlineClear(wrapper, FilterType.CategoryTree, active);
                if (this.position === FilterPosition.Top) {
                    this.reposition(this.filterViewGroupCache.groupByKey[viewGroupKey]);
                }
                return (values || []).length > 0;
            },
            updateHandlers(onChange, onExpand, commitMethod, discardMethod) {
                self.onChange = onChange;
                self.onExpand = onExpand;
                self.commitMethod = commitMethod;
                self.discardMethod = discardMethod;
            }
        };
    }
    appendApplyButton(viewGroupKey, content) {
        if (this.position === FilterPosition.Top) {
            const $btn = sxQuery('<button type="button" class="ss360-n-button ss360-filter__apply-group-button">Apply</button>');
            $btn.text(this.pluginConfiguration.filters.applyButtonLabel);
            $btn.on('click', () => {
                this.toggleGroup(viewGroupKey, true);
                if (this.commitMethod) {
                    this.commitMethod();
                }
            });
            const $wrap = sxQuery('<div class="ss360-filter__apply-group"></div>');
            $wrap.append($btn);
            content.append($wrap);
        }
    }
}
export default FilterGroupBuilder;
